<template>
	<div class="form-preview-page">
		<div class="form-content" ref="formContent">
			<div class="text-center mb-4">
				<h1>{{ fullTitle }}</h1>
			</div>
			
			<div v-if="formPreview.showGenerator && formPreview.sections.length > 0" class="form-preview-sections">
				<div v-for="(section, index) in formPreview.sections" :key="index" class="form-section">
					<h2 v-if="section.name" class="section-header">{{ section.name.toUpperCase() }}</h2>
					<vue-form-generator
						:disabled="true"
						:name="'previewGenerator-' + index"
						:model="section.model"
						:schema="section.schema"
						:options="formPreview.formOptions"
					/>
				</div>
			</div>
			
			<div v-else-if="!loading" class="text-center pa-4">
				<v-alert type="error">
					Nie udało się załadować formularza
				</v-alert>
			</div>
			
			<div v-else class="text-center pa-4">
				<v-progress-circular indeterminate size="64"></v-progress-circular>
				<p class="mt-3">Ładowanie formularza...</p>
			</div>
		</div>
	</div>
</template>

<script>
	import Vue from "vue";
	import VueFormGenerator from "vue-form-generator";
	import {PublicContest} from "@/api/generated/PublicContest";
	import {genericFormUtil} from "@/utils/genericFormUtil";

	Vue.use(VueFormGenerator);

	export default {
		name: "FormPreviewView",
		
		components: {
			'vue-form-generator': VueFormGenerator.component
		},

		data() {
			return {
				loading: true,
				formTitle: '',
				contestName: '',
				formPreview: {
					showGenerator: false,
					sections: [],
					formOptions: {
						validateAfterLoad: false,
						validateAfterChanged: false,
						validateAsync: false
					}
				}
			};
		},

		computed: {
			contestId() {
				return this.$route.params.contestId;
			},
			formType() {
				return this.$route.params.formType;
			},
			reportId() {
				return this.$route.params.reportId;
			},
			isVolunteerReport() {
				return this.formType === 'volunteer-report' && this.reportId;
			},
			fullTitle() {
				if (this.contestName) {
					return this.formTitle + ' w konkursie ' + this.contestName;
				}
				return this.formTitle;
			}
		},

		mounted() {
			this.loadFormPreview();
		},

		methods: {
			loadFormPreview() {
				this.loading = true;
				
				if (this.isVolunteerReport) {
					// For volunteer reports, load from report endpoint
					PublicContest.getReportForm(this, this.reportId, response => {
						if (response.volunteerReportSchema) {
							const form = genericFormUtil.decodeSchema(response.volunteerReportSchema);
							this.formTitle = `Raport wolontariusza - ${response.name || 'Formularz'}`;
							this.applyGenericFormPreview(form);
						} else {
							this.$messagesBus.$emit("error", "Nie udało się załadować formularza");
						}
						this.loading = false;
					}, error => {
						this.$messagesBus.$emit("error", "Nie udało się załadować formularza", error);
						this.loading = false;
					});
				} else {
					// For other forms, load from form endpoint
					PublicContest.getContestName(this, this.contestId, response => {
						this.contestName = response.name;
					}, error => {
						this.$messagesBus.$emit("error", "Nie udało się załadować nazwy konkursu", error);
					});
					PublicContest.getForm(this, this.contestId, this.formType, response => {
						if (response.form) {
							const form = genericFormUtil.decodeSchema(response.form);
							this.formTitle = this.getFormTypeLabel(this.formType);
							this.applyGenericFormPreview(form);
						} else {
							this.$messagesBus.$emit("error", "Nie udało się załadować formularza");
						}
						this.loading = false;
					}, error => {
						this.$messagesBus.$emit("error", "Nie udało się załadować formularza", error);
						this.loading = false;
					});
				}
			},

			applyGenericFormPreview(form) {
				if (!form || !form.sections || form.sections.length === 0) {
					this.$messagesBus.$emit("error", "Formularz jest pusty");
					return;
				}

				// Build empty model from schema
				const fullModel = genericFormUtil.buildModelFromSchema(form);

				// Process all sections
				this.formPreview.sections = [];
				for (let sectionIndex = 0; sectionIndex < form.sections.length; sectionIndex++) {
					const schema = genericFormUtil.getSectionSchemaFromSchema(form, sectionIndex, true); // true = disabled
					const sectionModel = fullModel.sections && fullModel.sections[sectionIndex] ? fullModel.sections[sectionIndex].fields : {};
					const sectionName = form.sections[sectionIndex].name;
					
					let finalSchema;
					if (genericFormUtil.sectionSchemaContainsConditionalField(schema)) {
						finalSchema = genericFormUtil.filterSchemaByConditionalFieldsValue(schema, sectionModel);
					} else {
						finalSchema = schema;
					}

					// Only add section if it has any visible groups
					if (finalSchema && finalSchema.groups && finalSchema.groups.length > 0) {
						this.formPreview.sections.push({
							name: sectionName,
							model: sectionModel,
							schema: finalSchema
						});
					}
				}

				this.formPreview.showGenerator = true;
			},

			getFormTypeLabel(formType) {
				const labels = {
					'OFFER': 'Formularz oferty',
					'MODULE_OFFER': 'Formularz oferty (moduł)',
					'FORMAL_EVALUATION': 'Opinia formalna',
					'INDIVIDUAL_EVALUATION': 'Indywidualna opinia członka komisji',
					'AGENCY_EVALUATION': 'Opinia placówki',
					'EXPERT_EVALUATION': 'Opinia eksperta',
					'COMMITTEE_COLLABORATION_EVALUATION': 'Ocena współpracy przez komisję',
					'FINAL_EVALUATION': 'Opinia końcowa',
					'COLLABORATION_EVALUATION': 'Ocena współpracy przez opiekuna',
					'MONITORING_REPORT': 'Raporty z monitoringu',
					'FINAL_REPORT': 'Sprawozdanie końcowe'
				};
				return labels[formType] || 'Formularz';
			}
		}
	};
</script>

<style>
	.form-preview-page {
		padding: 20px;
	}



	.form-content {
		max-width: 100%;
	}

	.section-header {
		font-size: 18px;
		font-weight: bold;
		margin: 20px 0 15px 0;
		text-transform: uppercase;
		page-break-after: avoid;
	}

	/* Form styling */
	.form-content .vue-form-generator .form-group {
		margin-bottom: 1rem;
		padding-bottom: 0;
		padding-top: 0;
	}

	.form-content .vue-form-generator .form-group > label {
		font-weight: 700;
		margin-bottom: 8px;
		display: block;
	}

	.form-content .vue-form-generator .field-wrap {
		display: block;
	}

	.form-content .vue-form-generator .field-wrap input,
	.form-content .vue-form-generator .field-wrap textarea,
	.form-content .vue-form-generator .field-wrap select {
		border: 1px solid #ccc;
		border-radius: 4px;
		padding: 10px;
		width: 100%;
		min-height: 43px;
		font-size: 13px;
		line-height: 16px;
		color: #555;
	}

	/* Print styles */
	@media print {
		.form-preview-page {
			padding: 0;
		}

		.form-content .vue-form-generator .form-group {
			display: block;
			page-break-inside: avoid;
			position: relative;
			margin-bottom: 15px;
		}

		.form-content .vue-form-generator .form-group > label {
			display: block;
			page-break-after: avoid;
			font-weight: bold;
			margin-bottom: 5px;
		}

		.form-content .vue-form-generator .field-wrap input,
		.form-content .vue-form-generator .field-wrap textarea,
		.form-content .vue-form-generator .field-wrap select {
			border: 1px solid #ccc !important;
			background: white !important;
			-webkit-print-color-adjust: exact !important;
			print-color-adjust: exact !important;
		}

		.form-content .v-text-field > .v-input__control > .v-input__slot > .v-text-field__slot textarea {
			display: none;
		}

		/* Checkbox styles for print */
		.form-content .vue-form-generator .form-group.field-checkbox {
			display: flex;
			align-items: center;
			flex-wrap: nowrap;
		}

		.form-content .vue-form-generator .form-group.field-checkbox .field-wrap {
			order: 1;
			margin-right: 8px;
			flex: 0 0 auto;
			margin-top: 2px;
		}

		.form-content .vue-form-generator .form-group.field-checkbox .field-wrap input {
			margin-left: 0;
			width: 14px;
			height: 14px;
			min-height: 14px;
		}

		.form-content .vue-form-generator .form-group.field-checkbox label {
			order: 2;
			flex: 1;
			margin-bottom: 0;
			font-size: 13px;
			line-height: 16px;
		}
	}
</style>